const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const PrivilegeCheckResult = require('@/lib/Privileges/PrivilegeCheckResult');
const { md5Hash } = require('@/functions/hash');
const { MODAL_NAMES } = require('@/vue/modals/MODAL_NAMES');
const { toTitleCase } = require('@/functions/strings');

class PrivilegesManager {
    /**
     * @param {Hydrator} hydrator
     * @param {NavigationService} navigationService
     * @param {PrivilegesRepository} privilegesRepository
     * @param {UserService} userService
     * @param {VueModalFactory} vueModalFactory
     */
    constructor(
        hydrator,
        navigationService,
        privilegesRepository,
        userService,
        vueModalFactory
    ) {
        this.hydrator = hydrator;
        this.navigationService = navigationService;
        this.privilegesRepository = privilegesRepository;
        this.userService = userService;
        this.vueModalFactroy = vueModalFactory;

        this.log = logFactory('Privileges');
    }

    /**
     * Return the set of privileges for the given userID.
     *
     * @param {number} userID
     *
     * @returns {Promise<Privileges>}
     */
    findPrivilegesByUserID(userID) {
        return this.privilegesRepository.find(userID);
    }

    /**
     * @returns {Promise<Privileges>}
     */
    findCurrentUserPrivileges() {
        if (!this.userService.currentUser || !this.userService.currentUser.ID) {
            return Promise.reject(new Error('There is no user logged in.'));
        }

        return this.findPrivilegesByUserID(this.userService.currentUser.ID);
    }

    /**
     * Return the set of privileges for the given username.
     *
     * @param {string} username
     *
     * @returns {Promise<Privileges>}
     */
    findPrivilegesByUsername(username) {
        return this.privilegesRepository.findAll()
            .then((allPrivileges) => {
                let privileges = allPrivileges.find((p) => p.username === username);
                if (!privileges) {
                    throw new Error(`Privileges for username '${username}' were not found.`);
                }

                return privileges;
            });
    }

    /**
     * Return the set of privileges for the given pin.
     *
     * @param {string} pin
     *
     * @returns {Promise<Privileges>}
     */
    findPrivilegesByPin(pin) {
        let hash = md5Hash(String(pin).toUpperCase(), true);
        return this.privilegesRepository.findAll()
            .then((allPrivileges) => {
                let privileges = allPrivileges.find((p) => p.pin === hash);
                if (!privileges) {
                    throw new Error('Privileges for that PIN were not found.');
                }

                return privileges;
            });
    }

    /**
     * Return the set of privileges for the given RFID tag ID.
     *
     * @param {string} rfidTagId
     *
     * @returns {Promise<Privileges>}
     */
    findPrivilegesByRfidTagId(rfidTagId) {
        let hash = md5Hash(String(rfidTagId).toUpperCase(), true);
        return this.privilegesRepository.findAll()
            .then((allPrivileges) => {
                let privileges = allPrivileges.find((p) => p.rfidTagId === hash);
                if (!privileges) {
                    throw new Error('Privileges for that RFID tag were not found.');
                }

                return privileges;
            });
    }

    /**
     * Method to find a users privileges given an access method and the value of
     * the relevant "thing". Eg 'pin' & '1234'.
     *
     * @param {string} method One of 'pin', 'rfid-tag' or any future value.
     * @param {string} value The value of the PIN, tag, or whatever.
     *
     * @returns {Promise<Privileges>}
     */
    findPrivileges(method, value) {
        // Return the value of the specialised function, depending on the method.
        switch (method) {
            case 'userID':
                return this.findPrivilegesByUserID(value);
            case 'username':
                return this.findPrivilegesByUsername(value);
            case 'pin':
                return this.findPrivilegesByPin(value);
            case 'rfid-tag':
                return this.findPrivilegesByRfidTagId(value);
            default:
                // Didn't recognise the method - return "didn't match a user".
                return Promise.reject(new Error('Invalid method.'));
        }
    }

    /**
     * Returns a promise which is resolved if the current user has the specified privilege.
     * If they do not, prompts for supervisor PIN and resolves the promise if an appropriate PIN in entered.
     * The promise will return the user ID of the user that granted the privilege if appropriate.
     * The promise will be rejected if the user does not have the privilege and has not been granted it.
     *
     * A user has a privilege if *any* of the View/Edit/Add/Delete options are checked for that privilege
     * in the Back Office (no need to check them all).
     *
     * @param {string} privilegeName
     *
     * @return {Promise<PrivilegeCheckResult>}
     */
    requirePrivilege(privilegeName) {
        this.log.debug('Checking for ' + privilegeName + ' privilege.');

        return this.checkCurrentUserPrivilege(privilegeName)
            .catch((err) => {
                this.log.debug(err);
                return this.showPinRequest(privilegeName)
                    .then((elevatedUserID) => {
                        let result = new PrivilegeCheckResult(privilegeName);
                        result.granted = true;
                        result.grantedBy = elevatedUserID;
                        return result;
                    });
            });
    }

    /**
     * If the current user does not have the specified privilege redirect to the 403 page.
     *
     * @param {string} privilegeName
     *
     * @return {Promise} Returns a promise that is resolved if the current user has the privilege, or rejected
     *              if they do not and the user has been redirected.
     *              Both receive a PrivilegeCheckResult object.
     */
    requirePrivilegeOr403(privilegeName) {
        return this.checkCurrentUserPrivilege(privilegeName)
            .catch((err) => {
                this.navigationService.view403();

                throw err;
            });
    }

    /**
     * Check if the logged in user has the specified privilege, or that privilege was
     * temporarily granted to them by a supervisor.
     *
     * @param {string} privilegeName Plaintext privilege name
     *
     * @return {Promise<PrivilegeCheckResult>}
     */
    checkCurrentUserPrivilege(privilegeName) {
        return this.findCurrentUserPrivileges()
            .then((privileges) => {
                let privilegeHash = this.generatePrivilegeHash(privilegeName);
                let result = new PrivilegeCheckResult(privilegeName);

                if (privileges.hasPrivilege(privilegeHash)) {
                    // The logged-in user has the privilege anyway, so we're good to go.
                    // Return boolean true.
                    result.granted = true;
                    // The current user had this privilege, so it was granted by themselves.
                    result.grantedBy = privileges.userID;
                    return result;
                }

                // NOTE: Temporary privileges are no longer used because it was pointless. Previously the
                // PinRequestCtrl would set the logged in user's temporary privilege only for it to be checked
                // here, then it would be immediately cleared.
                // That has been replaced by simply resolving if a valid user puts their details in the PinRequestCtrl.
                // The temporary privilege functionality has been left in in case we want to use it again in future.
                // An enhancement could be to have the temporary privilege granted for 1 minute so a supervisor does
                // not have to re-enter their PIN within 1 minute.
                let hasTemporaryPrivilegeGrantedBy = privileges.hasTemporaryPrivilege(privilegeHash);
                if (hasTemporaryPrivilegeGrantedBy !== false) {
                    // The logged in user has recently been temporarily granted this privilege by a supervisor.
                    // Return the user ID that granted it.
                    result.granted = true;
                    result.grantedBy = hasTemporaryPrivilegeGrantedBy;
                    return result;
                }

                throw new Error(`${privileges.username} does not have the ${privilegeName} privilege.`);
            });
    }

    /**
     * Check if a specific user has the specified privilege.
     *
     * @param {number} userID
     * @param {string} privilegeName Plaintext privilege name
     *
     * @returns {Promise<PrivilegeCheckResult>}
     */
    checkUserPrivilege(userID, privilegeName) {
        return this.findPrivilegesByUserID(userID)
            .then((privileges) => {
                let result = new PrivilegeCheckResult(privilegeName);

                if (privileges.hasPrivilege(this.generatePrivilegeHash(privilegeName))) {
                    result.granted = true;
                    result.grantedBy = privileges.userID;
                    return result;
                }

                throw new Error(`${privileges.username} does not have the ${privilegeName} privilege.`);
            });
    }

    /**
     * Performs the same check as checkCurrentUserPrivilege but returns a promise instead
     * of the result directly. The promise contains just a boolean, not who granted it.
     *
     * @param {string[]} privilegeNames Array of plaintext privilege names.
     *
     * @returns {Promise<Object.<string, PrivilegeCheckResult>>} When resolved, returns an object keyed by the plaintext
     *     privilege names each with a PrivilegeCheckResult as a value. Note that this promise is always resolved
     *     and you must check each to see if it was granted.
     */
    checkMultipleCurrentUserPrivileges(privilegeNames) {
        let results = {};
        return Promise
            .all(
                privilegeNames.map((privilegeName) => {
                    return this.checkCurrentUserPrivilege(privilegeName)
                        .then((result) => {
                            results[privilegeName] = result;
                        })
                        .catch(() => {
                            results[privilegeName] = new PrivilegeCheckResult(privilegeName);
                        });
                })
            )
            .then(() => {
                return results;
            });
    }

    /**
     * Show the pin request modal.
     * If a valid PIN is entered (or RFID scanned) for a user that has the requested privilege the promise will resolve
     * with the userID of that user.
     *
     * @param {string} privilegeName
     *
     * @return {Promise<Number>}
     */
    showPinRequest(privilegeName) {
        return this.vueModalFactroy.open(
            MODAL_NAMES.PIN_REQUEST,
            {
                privilegeName,
            }
        );
    }

    /**
     * Check if a user (not necessarily the current user) has the specified privilege name. The user is identified
     * by their pin,username, or rfid tag.
     *
     * @param {string} authMethod
     * @param {string} authValue
     * @param {string} privilegeName
     * @returns {Promise<PrivilegeCheckResult>}
     */
    checkUserHasPrivilegeByAuthMethod(authMethod, authValue, privilegeName) {
        let privilegeHash = this.generatePrivilegeHash(privilegeName);

        // Resolve if the set of privileges for the user with the matching PIN has the
        // privilege we're asking for.
        return this
            .findPrivileges(authMethod, authValue)
            .then((pinPrivs) => {
                if (!pinPrivs.hasPrivilege(privilegeHash)) {
                    throw new Error(`${pinPrivs.username} does not have the ${privilegeName} privilege.`);
                }

                let result = new PrivilegeCheckResult(privilegeName);
                result.grantedBy = pinPrivs.userID;
                result.granted = true;

                return result;
            });
    }

    /**
     * @private
     * @param {string} privilegeName
     * @returns {string}
     */
    generatePrivilegeHash(privilegeName) {
        return md5Hash(privilegeName);
    }

    /**
     * @param privilegeName
     * @returns {string}
     */
    generateFriendlyPrivilegeName(privilegeName) {
        let friendlyPrivilegeName = privilegeName.replace(/_/g, ' ');

        friendlyPrivilegeName = friendlyPrivilegeName.substring(0, 1).toUpperCase()
        + friendlyPrivilegeName.substring(1);

        return toTitleCase(friendlyPrivilegeName);
    }
}

module.exports = PrivilegesManager;
